import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { useApoAuth } from "../../hooks";
import { createAttachment } from "./api/createAttachment";
import { type ChatAttachment, getAttachments } from "./api/getAttachments";
import { getChatSessions } from "./api/getChatSessions";
import { initializeChatSession } from "./api/initializeChatSession";
import { getChatMessages } from "./api/messages";
import { AttachmentsBar } from "./components/AttachmentsBar";
import { AttachmentUploadingModal } from "./components/AttachmentUploadingModal";
import { Footer } from "./components/Footer";
import { Header } from "./components/Header";
import { Message } from "./components/Message";
import type { Message as MessageType } from "./components/Message/types";
import { Sidebar } from "./components/Sidebar";
import { SSEChatClient } from "./helpers";

function QuotationRequestChat() {
  const { user, source_entity } = useApoAuth();
  const queryClient = useQueryClient();
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const hasScrolledForSessionRef = useRef<string | null>(null);

  const username = user?.name || user?.email || "";
  const chatSessionsQueryKey = ["chatSessions", username];

  // Fetch chat sessions
  const { data: chatSessionsData } = useQuery({
    queryKey: chatSessionsQueryKey,
    queryFn: () => getChatSessions(username),
    enabled: !!username,
    refetchOnWindowFocus: false,
  });

  // Find the selected session name
  const selectedSessionName =
    chatSessionsData?.sessions?.find((session) => session.uid === selectedSessionId)?.name || null;

  // Fetch messages for the selected session
  const { data: messagesData } = useQuery({
    queryKey: ["chatMessages", selectedSessionId],
    queryFn: () => getChatMessages(selectedSessionId ?? ""),
    enabled: !!selectedSessionId,
    refetchOnWindowFocus: false,
  });

  const messages: MessageType[] = messagesData?.data?.messages || [];

  const assistantMessagesCount = messages.filter((msg) => msg.role === "assistant").length;
  const userMessagesCount = messages.filter((msg) => msg.role === "user").length;

  const hasEnoughAssistantMessages =
    assistantMessagesCount >= userMessagesCount && userMessagesCount > 0;

  // Fetch attachments for the selected session
  const { data: attachmentsData } = useQuery({
    queryKey: ["chatAttachments", selectedSessionId],
    queryFn: () => getAttachments(selectedSessionId ?? ""),
    enabled: !!selectedSessionId,
    refetchOnWindowFocus: false,
  });

  const attachments: ChatAttachment[] = attachmentsData?.data?.attachments || [];

  // Set sidebar state based on screen size - open on desktop, closed on mobile
  useEffect(() => {
    const checkScreenSize = () => {
      const isDesktop = window.innerWidth >= 768; // md breakpoint
      setIsSidebarOpen(isDesktop);
    };

    // Set initial state
    checkScreenSize();

    // Listen for resize events
    window.addEventListener("resize", checkScreenSize);
    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  // Disable scrolling for the pages element when component mounts
  useEffect(() => {
    const pagesElement = document.getElementById("pages");
    if (!pagesElement) return;

    const originalOverflow = pagesElement.style.overflow;
    pagesElement.style.overflow = "hidden";

    // Restore original overflow when component unmounts
    return () => {
      pagesElement.style.overflow = originalOverflow;
    };
  }, []);

  // Reset scroll tracking when session changes
  useEffect(() => {
    if (selectedSessionId !== hasScrolledForSessionRef.current) {
      hasScrolledForSessionRef.current = null; // Reset to allow scrolling for new session
    }
  }, [selectedSessionId]);

  // Auto-scroll to bottom when messages change
  // Only scroll if user is near the bottom to avoid interrupting manual scrolling
  useEffect(() => {
    if (!messagesContainerRef.current || messages.length === 0) return;

    const container = messagesContainerRef.current;
    const scrollThreshold = 150; // pixels from bottom
    const isNearBottom =
      container.scrollHeight - container.scrollTop - container.clientHeight < scrollThreshold;

    // Check if this is the initial load for this session (haven't scrolled for this session yet)
    const isInitialLoad = hasScrolledForSessionRef.current !== selectedSessionId;

    // Auto-scroll if: user is near bottom, streaming, or initial session load
    if (isNearBottom || isLoading || isInitialLoad) {
      // Use requestAnimationFrame to ensure DOM has updated
      requestAnimationFrame(() => {
        if (messagesEndRef.current) {
          // Use scrollIntoView with instant behavior during streaming to avoid stuttering
          messagesEndRef.current.scrollIntoView({
            behavior: isLoading ? "auto" : "smooth",
            block: "end",
          });
          // Mark that we've scrolled for this session
          if (selectedSessionId) {
            hasScrolledForSessionRef.current = selectedSessionId;
          }
        }
      });
    }
  }, [messages.length, messages, isLoading, selectedSessionId]);

  const toggleDarkMode = () => {
    setIsDarkMode((prev) => !prev);
  };

  const createSessionMutation = useMutation({
    mutationFn: (data: { source_entity: string; username: string; user_email: string }) =>
      initializeChatSession(data),
    onSuccess: (response) => {
      if (response.data?.chat_session_uid) {
        // Set the newly created session as selected
        setSelectedSessionId(response.data.chat_session_uid);
        // Refresh chat sessions list
        queryClient.invalidateQueries({ queryKey: chatSessionsQueryKey });
      }
    },
  });

  const uploadAttachmentMutation = useMutation({
    mutationFn: ({ chat_session_uid, file }: { chat_session_uid: string; file: File }) =>
      createAttachment(chat_session_uid, file),
  });

  // Handle upload success
  useEffect(() => {
    if (uploadAttachmentMutation.isSuccess) {
      toast.success("File uploaded successfully.");
      // Refetch attachments list
      queryClient.invalidateQueries({ queryKey: ["chatAttachments", selectedSessionId] });
      // Refetch messages to include any attachment-related updates
      queryClient.invalidateQueries({ queryKey: ["chatMessages", selectedSessionId] });
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      uploadAttachmentMutation.reset();
    }
  }, [
    uploadAttachmentMutation.isSuccess,
    uploadAttachmentMutation.reset,
    queryClient,
    selectedSessionId,
  ]);

  // Handle upload error
  useEffect(() => {
    if (uploadAttachmentMutation.isError) {
      const error = uploadAttachmentMutation.error as {
        response?: { data?: { detail?: string } };
      };
      const errorMessage =
        error?.response?.data?.detail || "Failed to upload file. Please try again.";
      toast.error(errorMessage);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      uploadAttachmentMutation.reset();
    }
  }, [
    uploadAttachmentMutation.isError,
    uploadAttachmentMutation.error,
    uploadAttachmentMutation.reset,
  ]);

  const handleCreateNewSession = () => {
    if (!user || !source_entity) {
      return;
    }

    createSessionMutation.mutate({
      source_entity,
      username: user.name || user.email || "",
      user_email: user.email || "",
    });
  };

  const handleSendMessage = async (msg?: string) => {
    if (!selectedSessionId || !user || !source_entity) {
      return;
    }

    // Use provided message or read from input
    let message: string;
    if (msg !== undefined) {
      message = msg.trim();
    } else {
      if (!inputRef.current) {
        return;
      }
      message = inputRef.current.value.trim();
    }

    if (!message) {
      return;
    }

    // Clear input only if we're using the input (not a provided message)
    if (msg === undefined && inputRef.current) {
      inputRef.current.value = "";
    }

    setIsLoading(true);

    // Capture the current session ID at the start
    const currentSessionId = selectedSessionId;

    // Add user message optimistically
    const userMessage: MessageType = {
      chat_session_uid: currentSessionId,
      id: Date.now().toString(),
      content: message,
      role: "user",
      timestamp: new Date().toISOString(),
    };

    // Add placeholder assistant message
    const placeholderMessageId = `placeholder-${Date.now()}`;
    const placeholderMessage: MessageType = {
      chat_session_uid: currentSessionId,
      id: placeholderMessageId,
      content: "",
      role: "assistant",
      timestamp: new Date().toISOString(),
    };

    // Update cache optimistically
    queryClient.setQueryData<{ data: { messages: MessageType[] } }>(
      ["chatMessages", currentSessionId],
      (oldData) => {
        if (!oldData) {
          return {
            data: {
              messages: [userMessage, placeholderMessage],
            },
          };
        }
        return {
          ...oldData,
          data: {
            ...oldData.data,
            messages: [...oldData.data.messages, userMessage, placeholderMessage],
          },
        };
      },
    );

    // Track the actual session ID used (may change during streaming)
    let actualSessionId = currentSessionId;

    await SSEChatClient(
      source_entity,
      user.name || user.email || "",
      user.email || "",
      currentSessionId,
      message,
      (streamedMessage: string, chatSessionUid: string) => {
        // Update selected session ID if it changes
        if (chatSessionUid !== currentSessionId) {
          setSelectedSessionId(chatSessionUid);
          actualSessionId = chatSessionUid;
        }

        // Update the placeholder message in real-time as SSE streams
        queryClient.setQueryData<{ data: { messages: MessageType[] } }>(
          ["chatMessages", chatSessionUid || actualSessionId],
          (oldData) => {
            if (!oldData) return oldData;

            const updatedMessages = oldData.data.messages.map((msg) => {
              if (msg.id === placeholderMessageId) {
                return {
                  ...msg,
                  content: streamedMessage,
                  chat_session_uid: chatSessionUid || msg.chat_session_uid,
                };
              }
              return msg;
            });

            return {
              ...oldData,
              data: {
                ...oldData.data,
                messages: updatedMessages,
              },
            };
          },
        );
      },
      () => {
        setIsLoading(false);
        // Refresh messages after SSE stream is complete to ensure consistency
        queryClient.invalidateQueries({ queryKey: ["chatMessages", actualSessionId] });
      },
    );
  };

  const handleGenerateQuotation = () => {
    if (!selectedSessionId) {
      toast.error("Please select a chat session first.");
      return;
    }
    handleSendMessage("<--- Generate Final Quotation Request --->");
  };

  return (
    <div
      className={[
        "mt-[var(--navbar-height)]",
        "h-[calc(100vh-var(--navbar-height))]",
        "w-full",
        "overflow-x-hidden",
        "grid",
        isSidebarOpen
          ? ["grid-cols-[0_1fr]", "md:grid-cols-[280px_1fr]"].join(" ")
          : "grid-cols-[0_1fr]",
        "transition-all",
        "duration-300",
        "relative",
        isDarkMode ? "bg-gray-900" : "bg-white",
      ].join(" ")}
    >
      {/* Mobile overlay */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
          onClick={() => setIsSidebarOpen(false)}
          aria-hidden="true"
        />
      )}
      {/* Sidebar */}
      <Sidebar
        isDarkMode={isDarkMode}
        isSidebarOpen={isSidebarOpen}
        chatSessionsData={chatSessionsData}
        selectedSessionId={selectedSessionId}
        setIsSidebarOpen={setIsSidebarOpen}
        setSelectedSessionId={setSelectedSessionId}
        onCreateNewSession={handleCreateNewSession}
      />
      {/* Main Content Area */}
      <div
        className={[
          "grid",
          "grid-rows-[auto_1fr_auto]",
          "w-full",
          "h-full",
          "min-w-0",
          "overflow-x-hidden",
        ].join(" ")}
      >
        {/* Header Section */}
        <Header
          isDarkMode={isDarkMode}
          isSidebarOpen={isSidebarOpen}
          selectedSessionName={selectedSessionName}
          setIsSidebarOpen={setIsSidebarOpen}
          toggleDarkMode={toggleDarkMode}
        />

        {/* Messages/Content Area */}
        <main
          ref={messagesContainerRef}
          className={`grid grid-rows-[1fr] overflow-y-auto overflow-x-hidden p-2 md:p-4 ${isDarkMode ? "bg-gray-900" : "bg-gray-50"}`}
          style={{ scrollBehavior: isLoading ? "auto" : "smooth" }}
        >
          <div className="grid auto-rows-max gap-4 max-w-4xl mx-auto w-full min-w-0">
            {messages.map((msg, index) => {
              // Check if this is the last assistant message and it's being streamed
              const isLastAssistantMessage =
                msg.role === "assistant" && index === messages.length - 1;
              const isStreaming = isLoading && isLastAssistantMessage;

              return (
                <Message
                  key={msg.id}
                  message={msg}
                  isDarkMode={isDarkMode}
                  isStreaming={isStreaming}
                />
              );
            })}
            {/* Invisible element at the bottom to scroll to */}
            <div ref={messagesEndRef} />
          </div>
        </main>

        {/* Attachments Bar */}
        {selectedSessionId && (
          <AttachmentsBar
            attachments={attachments}
            chatSessionId={selectedSessionId}
            isDarkMode={isDarkMode}
          />
        )}

        {/* Input Section */}
        <Footer
          isDarkMode={isDarkMode}
          selectedSessionId={selectedSessionId}
          fileInputRef={fileInputRef}
          inputRef={inputRef}
          uploadAttachmentMutation={uploadAttachmentMutation}
          isLoading={isLoading}
          showGenerateQuotationButton={hasEnoughAssistantMessages}
          onSendMessage={handleSendMessage}
          onGenerateQuotation={handleGenerateQuotation}
        />
      </div>

      {/* Attachment Uploading Modal */}
      {uploadAttachmentMutation.isLoading && <AttachmentUploadingModal isDarkMode={isDarkMode} />}
    </div>
  );
}

export { QuotationRequestChat };
